import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getBreakpointUp,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { Location } from 'history'
import React from 'react'
import CheckFilledLoD from 'src/assets/svgs/CheckFilledLoD.svg'
import Box from 'src/components/Box/Box'
import Spotlight from 'src/components/Spotlight/Spotlight'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import Trns from 'src/localization/Trns'
import Lnk from 'src/navigation/Lnk'
import { LocationState } from 'src/redux/createRootReducer'
import { css } from 'styled-components'

interface ObjectsWithCountsBoxProps {
  className?: string
  objects: BoxProps[]
  orgBookkeeper?: boolean
  bottomBox?: boolean
  middleBox?: boolean
}

type BoxProps = BoxPropsAriaSource | BoxPropsAriaLabel

interface BoxPropsShared {
  title: React.ReactElement
  id?: string
  subtitle?: React.ReactElement
  objectName: React.ReactElement
  count?: number
  isRed?: boolean
  isGray?: boolean
  to: string | Location<LocationState>
  hasTooltip?: boolean
  setTooltip?: (val: boolean) => void
  tooltipTitle?: React.ReactElement
  tooltipText?: React.ReactElement
}

interface BoxPropsAriaSource extends BoxPropsShared {
  ariaLabelSource: string
  ariaLabel?: undefined
}

interface BoxPropsAriaLabel extends BoxPropsShared {
  ariaLabel: string
  ariaLabelSource?: undefined
}

const ObjectsWithCountsBox: React.FC<
  React.PropsWithChildren<ObjectsWithCountsBoxProps>
> = (props): React.ReactElement => {
  return (
    <Box
      className={props.className}
      noPadding
      orgBookkeeper={props.orgBookkeeper}
      bottomBox={props.bottomBox}
    >
      {props.objects.map((obj, i) => (
        <div
          key={i}
          onClick={(): void | null =>
            obj.setTooltip !== undefined ? obj.setTooltip(false) : null
          }
          onKeyPress={(): null => {
            return null
          }}
          role={'button'}
          tabIndex={0}
        >
          <Lnk
            id={obj.id}
            to={obj.to}
            className={'entry'}
            {...(obj.ariaLabelSource !== undefined
              ? { ariaLabelSource: obj.ariaLabelSource }
              : { ariaLabel: obj.ariaLabel })}
            css={css`
              display: flex;
              justify-content: space-between;
              padding: ${styleConstants.heights.cardPadding}
                ${styleConstants.widths.cardPaddingXSMUp};

              ${getBreakpointOnly(breakpointSizes.XXSM)(css`
                padding: ${styleConstants.heights.cardPadding}
                  ${styleConstants.widths.cardPaddingXXSMOnly};
              `)}

              &:nth-child(n+2) {
                border-top: 1px solid ${styleConstants.colors.gray_light};
              }

              ${getBreakpointUp(breakpointSizes.XXSM)(css`
                &:hover {
                  * span {
                    ${getFontCss(
                      20,
                      themeConstants.colors.cta,
                      styleConstants.fontFamilies.regular,
                    )}
                  }

                  * svg {
                    transform: scale(calc(20 / 24)) translateX(4px);
                  }
                }
              `)}
            `}
          >
            <span
              css={css`
                @include font-20-primary-bold;
                ${getFontCss(
                  20,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )}

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )}
                `)}
              `}
            >
              {obj.title}
            </span>
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <span
                className={'count'}
                css={css`
                  ${getFontCss(
                    20,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )}
                  margin-right: 10px;
                  transition: color ${styleConstants.transitionSpeed.fast}
                    ease-out;

                  ${!!obj.isRed &&
                  css`
                    ${getFontCss(
                      20,
                      styleConstants.colors.red,
                      styleConstants.fontFamilies.regular,
                    )}
                  `}
                  ${(obj.count === 0 || !!obj.isGray) &&
                  css`
                    ${getFontCss(
                      20,
                      styleConstants.colors.gray_dark,
                      styleConstants.fontFamilies.regular,
                    )}
                  `}

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.highlight,
                      styleConstants.fontFamilies.regular,
                    )}

                    ${!!obj.isRed &&
                    css`
                      ${getFontCss(
                        16,
                        styleConstants.colors.red,
                        styleConstants.fontFamilies.regular,
                      )}
                    `}
                  ${(obj.count === 0 || !!obj.isGray) &&
                    css`
                      ${getFontCss(
                        16,
                        styleConstants.colors.gray_dark,
                        styleConstants.fontFamilies.regular,
                      )}
                    `}
                  `)}
                `}
              >
                {obj.count !== undefined && (
                  <Trns
                    source={'${count} ${objectName}'}
                    expressions={{
                      count: `${obj.count}`,
                      objectName: obj.objectName,
                    }}
                  />
                )}
                {obj.count === undefined && obj.objectName}
              </span>
              <SvgChevronRight
                css={css`
                  transition: transform ${styleConstants.transitionSpeed.fast}
                    ease-out;
                  transform: scale(calc(20 / 24));
                `}
              />
            </div>
          </Lnk>
          {obj.hasTooltip && obj.setTooltip && (
            <div
              css={css`
                position: absolute;
                width: 100%;
              `}
            >
              <Spotlight
                imageSrc={CheckFilledLoD}
                tooltip
                smallerText
                title={obj.tooltipTitle || <></>}
                subtitle={obj.tooltipText || <></>}
                cookieFunction={obj.setTooltip}
                css={css`
                  position: absolute;
                  z-index: 1;
                  width: 70%;
                  max-width: 350px;
                  bottom: -70px;
                  right: -100px;
                  ${getBreakpointDown(breakpointSizes.SM)(css`
                    bottom: -70px;
                    left: 20px;
                  `)}
                `}
              />
            </div>
          )}
        </div>
      ))}
    </Box>
  )
}

export default ObjectsWithCountsBox
